import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FaSearch,
    FaChevronLeft,
    FaDownload,
    FaStar,
    FaRegStar,
    FaEye,
    FaTrash,
    FaSortAmountDown,
    FaSortAmountUp,
    FaCalendarAlt,
    FaMagic,
    FaFileAlt
} from 'react-icons/fa';
import { getTailoringHistory, downloadTailoredResume } from '../../services/tailor';
import './ResumeHistory.css';

const ResumeHistory = () => {

    useEffect(() => {
        document.title += ' - Resumes';
        // This will clean up the title when component unmounts
        return () => {
            document.title = 'CVance'; // Default title
        };
    }, []);

    const [resumes, setResumes] = useState([]);
    const [filteredResumes, setFilteredResumes] = useState([]);
    const [favoriteResumes, setFavoriteResumes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortOrder, setSortOrder] = useState('newest');
    const [downloadingId, setDownloadingId] = useState(null);
    const [activeFilter, setActiveFilter] = useState('all');
    const [totalResumes, setTotalResumes] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [moreResultsLoading, setMoreResultsLoading] = useState(false);

    // Fetch resume history on component mount
    useEffect(() => {
        const fetchResumeHistory = async () => {
            try {
                setLoading(true);
                const data = await getTailoringHistory();
                setResumes(data.history || []);
                setFilteredResumes(data.history || []);
                setTotalResumes(data.total_resumes || 0);
                setHasMore((data.history?.length || 0) >= 10 && (data.history?.length || 0) < (data.total_resumes || 0));

                // Load favorites from localStorage
                const storedFavorites = localStorage.getItem('favoriteResumes') || '[]';
                const favoritesArray = JSON.parse(storedFavorites);
                setFavoriteResumes(favoritesArray);
            } catch (err) {
                setError('Failed to load resume history');
                console.error('Error fetching history:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchResumeHistory();
    }, []);

    // Load more resumes
    const loadMoreResumes = async () => {
        if (!hasMore || moreResultsLoading) return;

        try {
            setMoreResultsLoading(true);
            const nextOffset = (currentPage + 1) * 10;
            const data = await getTailoringHistory({ limit: 10, offset: nextOffset });
            
            if (data.history && data.history.length > 0) {
                const newResumes = [...resumes, ...data.history];
                setResumes(newResumes);
                setFilteredResumes(newResumes);
                setCurrentPage(currentPage + 1);
                setHasMore(newResumes.length < (data.total_resumes || 0));
            } else {
                setHasMore(false);
            }
        } catch (err) {
            setError('Failed to load more resumes');
            console.error('Error fetching more resumes:', err);
        } finally {
            setMoreResultsLoading(false);
        }
    };

    // Filter and sort resumes
    useEffect(() => {
        let results = [...resumes];

        // Apply search filter
        if (searchTerm) {
            results = results.filter(resume => {
                const positionTitle = resume?.job_analysis?.position_title || resume?.jobTitle || '';
                const company = resume?.job_analysis?.company_name || resume?.company || '';
                const searchLower = searchTerm.toLowerCase();

                return positionTitle.toLowerCase().includes(searchLower) ||
                    company.toLowerCase().includes(searchLower);
            });
        }

        // Apply type filter
        if (activeFilter === 'favorites') {
            results = results.filter(resume =>
                favoriteResumes.includes(resume.id || resume.session_id)
            );
        } else if (activeFilter === 'high-match') {
            results = results.filter(resume =>
                resume.match_score && resume.match_score >= 0.8
            );
        }

        // Apply sort
        results.sort((a, b) => {
            const dateA = new Date(a.created_at || a.createdAt);
            const dateB = new Date(b.created_at || b.createdAt);

            return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
        });

        setFilteredResumes(results);
    }, [searchTerm, activeFilter, sortOrder, resumes, favoriteResumes]);

    const handleDownloadResume = async (resumeId, event) => {
        event.preventDefault();
        event.stopPropagation();

        try {
            setDownloadingId(resumeId);
            await downloadTailoredResume(resumeId, "docx");
        } catch (error) {
            console.error('Error downloading resume:', error);
        } finally {
            setDownloadingId(null);
        }
    };

    const toggleFavorite = (resumeId, event) => {
        event.preventDefault();
        event.stopPropagation();

        let newFavorites;

        if (favoriteResumes.includes(resumeId)) {
            newFavorites = favoriteResumes.filter(id => id !== resumeId);
        } else {
            newFavorites = [...favoriteResumes, resumeId];
        }

        setFavoriteResumes(newFavorites);
        localStorage.setItem('favoriteResumes', JSON.stringify(newFavorites));
    };

    // Format date to relative time
    const formatRelativeTime = (dateString) => {
        if (!dateString) return '';

        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);

        if (diffInSeconds < 60) {
            return 'just now';
        }

        const diffInMinutes = Math.floor(diffInSeconds / 60);
        if (diffInMinutes < 60) {
            return `${diffInMinutes}m ago`;
        }

        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) {
            return `${diffInHours}h ago`;
        }

        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays < 30) {
            return `${diffInDays}d ago`;
        }

        // For older dates, return formatted date
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
    };

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.05
            }
        }
    };

    const itemVariants = {
        hidden: { y: 10, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { type: "spring", stiffness: 100, damping: 12 }
        },
        exit: {
            opacity: 0,
            y: 10,
            transition: { duration: 0.2 }
        }
    };

    const renderFilterOptions = () => (
        <div className="filter_options_rh">
            <button
                className={`filter_option_rh ${activeFilter === 'all' ? 'active' : ''}`}
                onClick={() => setActiveFilter('all')}
            >
                All Resumes
            </button>
            <button
                className={`filter_option_rh ${activeFilter === 'favorites' ? 'active' : ''}`}
                onClick={() => setActiveFilter('favorites')}
            >
                <FaStar className="option_icon_rh" /> Favorites
            </button>
            <button
                className={`filter_option_rh ${activeFilter === 'high-match' ? 'active' : ''}`}
                onClick={() => setActiveFilter('high-match')}
            >
                <span className="option_icon_rh">80%+</span> High Matches
            </button>
        </div>
    );

    return (
        <div className="history_page_rh">
            <div className="container">
                {/* Header */}
                <div className="history_header_rh">
                    <div className="header_left_rh">
                        <Link to="/dashboard" className="back_link_rh">
                            <FaChevronLeft /> Back to Dashboard
                        </Link>
                        <h1>Resume History</h1>
                    </div>

                    <Link to="/tailor" className="create_btn_rh">
                        <FaMagic /> Create New Resume
                    </Link>
                </div>

                {/* Stats */}
                <motion.div
                    className="resume_stats_rh"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <div className="stat_card_rh total_card_rh">
                        <FaFileAlt className="stat_icon_rh" />
                        <div>
                            <p className="stat_label_rh">Total</p>
                            <p className="stat_value_rh">{totalResumes}</p>
                        </div>
                    </div>

                    <div className="stat_card_rh favorites_card_rh">
                        <FaStar className="stat_icon_rh" />
                        <div>
                            <p className="stat_label_rh">Favorites</p>
                            <p className="stat_value_rh">{favoriteResumes.length}</p>
                        </div>
                    </div>

                    <div className="stat_card_rh match_card_rh">
                        <div className="stat_icon_rh check_icon_rh">✓</div>
                        <div>
                            <p className="stat_label_rh">High Matches</p>
                            <p className="stat_value_rh">
                                {resumes.filter(r => r.match_score && r.match_score >= 0.8).length}
                            </p>
                        </div>
                    </div>
                </motion.div>

                {/* Search and Filters */}
                <div className="controls_container_rh">
                    <div className="search_container_rh">
                        <FaSearch className="search_icon_rh" />
                        <input
                            type="text"
                            placeholder="Search by title or company..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search_input_rh"
                        />
                        {searchTerm && (
                            <button
                                className="clear_search_rh"
                                onClick={() => setSearchTerm('')}
                                aria-label="Clear search"
                            >
                                ×
                            </button>
                        )}
                    </div>

                    <div className="filters_row_rh">
                        {renderFilterOptions()}

                        <button
                            className="sort_btn_rh"
                            onClick={() => setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest')}
                            aria-label={`Sort by ${sortOrder === 'newest' ? 'oldest' : 'newest'}`}
                        >
                            {sortOrder === 'newest' ? <FaSortAmountDown /> : <FaSortAmountUp />}
                            <span>{sortOrder === 'newest' ? 'Newest' : 'Oldest'}</span>
                        </button>
                    </div>
                </div>

                {/* Results Count */}
                <div className="results_count_rh">
                    {filteredResumes.length > 0 ? (
                        <p>
                            Showing <span>{filteredResumes.length}</span> of <span>{totalResumes}</span> resumes
                            {activeFilter !== 'all' && (
                                <> • <em>{activeFilter === 'favorites' ? 'Favorites' : 'High Matches'}</em></>
                            )}
                        </p>
                    ) : (
                        <p>No results found</p>
                    )}
                </div>

                {/* Error Message */}
                {error && (
                    <div className="error_message_rh">
                        {error}
                    </div>
                )}

                {/* Resume List */}
                {loading ? (
                    <div className="loading_container_rh">
                        <div className="loading_spinner_rh"></div>
                        <p>Loading your resumes...</p>
                    </div>
                ) : filteredResumes.length === 0 && !error ? (
                    <div className="empty_state_rh">
                        <div className="empty_icon_rh">
                            <FaFileAlt />
                        </div>
                        <h2>No resumes found</h2>
                        <p>
                            {searchTerm || activeFilter !== 'all'
                                ? "Try adjusting your search or filter criteria"
                                : "Start tailoring resumes to see them here"}
                        </p>
                        <Link to="/tailor" className="create_action_btn_rh">
                            <FaMagic /> Create Your First Resume
                        </Link>
                    </div>
                ) : (
                    <motion.div
                        className="resume_list_rh"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        <AnimatePresence>
                            {filteredResumes.map((resume) => {
                                const resumeId = resume.session_id;
                                const isFavorite = favoriteResumes.includes(resumeId);
                                const title = (resume?.job_analysis?.position_title || resume?.jobTitle) || 'Untitled Resume';
                                const company = resume?.job_analysis?.company_name || resume?.company;
                                const dateCreated = resume.created_at || resume.createdAt;
                                const matchScore = resume.match_score ? Math.round(resume.match_score * 100) : null;

                                return (
                                    <motion.div
                                        key={resumeId}
                                        className="resume_card_rh"
                                        variants={itemVariants}
                                        whileHover={{ y: -2 }}
                                        layout
                                    >
                                        <div className="resume_content_rh">
                                            <div className="resume_info_rh">
                                                <h3 className="resume_title_rh">{title}</h3>
                                                {company && <p className="resume_company_rh">{company}</p>}
                                            </div>

                                            <div className="resume_meta_rh">
                                                {matchScore && (
                                                    <div className={`match_score_rh ${matchScore >= 80 ? 'high_match_rh' : ''}`}>
                                                        {matchScore}%
                                                    </div>
                                                )}

                                                <div className="date_created_rh" title={new Date(dateCreated).toLocaleString()}>
                                                    <FaCalendarAlt /> {formatRelativeTime(dateCreated)}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="action_buttons_rh">
                                            <button
                                                className={`action_btn_rh favorite_btn_rh ${isFavorite ? 'active' : ''}`}
                                                onClick={(e) => toggleFavorite(resumeId, e)}
                                                aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
                                            >
                                                {isFavorite ? <FaStar /> : <FaRegStar />}
                                            </button>

                                            <button
                                                className="action_btn_rh download_btn_rh"
                                                onClick={(e) => handleDownloadResume(resumeId, e)}
                                                disabled={downloadingId === resumeId}
                                                aria-label="Download resume"
                                            >
                                                {downloadingId === resumeId ? (
                                                    <div className="btn_spinner_rh"></div>
                                                ) : (
                                                    <FaDownload />
                                                )}
                                            </button>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </AnimatePresence>
                    </motion.div>
                )}

                {/* Load More Button */}
                {hasMore && !moreResultsLoading && filteredResumes.length > 0 && (
                    <div className="load_more_container_rh">
                        <button 
                            className={`load_more_btn_rh ${moreResultsLoading}`}
                            onClick={loadMoreResumes}
                            disabled={moreResultsLoading}
                        >
                            {moreResultsLoading ? 'Loading...' : 'Load More Resumes'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ResumeHistory;